import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TafraMark = ({ size = 84 }) => (
  <span
    className="relative inline-flex items-center justify-center logo-scan"
    style={{ width: size, height: size }}
  >
    <span
      className="absolute inset-[-30%] animate-breathe pointer-events-none"
      style={{
        background:
          "radial-gradient(circle, var(--accent-blue-glow), transparent 65%)",
        filter: "blur(8px)",
      }}
      aria-hidden
    />
    <img
      src="/tafra-icon.png"
      alt="TAFRA Energy logo"
      width={size}
      height={size}
      style={{ filter: "var(--logo-filter)", objectFit: "contain" }}
      className="relative z-10"
    />
  </span>
);

const IntroSplash = () => {
  // Only show the intro once per session (so navigation feels instant).
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("tafra_intro_done") === "1") return false;
    } catch (e) {
      // sessionStorage may be blocked — fall through and show intro
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("Intro state read skipped:", e?.message || e);
      }
    }
    return true;
  });

  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setVisible(false);
      try {
        sessionStorage.setItem("tafra_intro_done", "1");
      } catch (e) {
        // sessionStorage may be blocked — safe to ignore
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("Intro state persist skipped:", e?.message || e);
        }
      }
    }, 1500);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed inset-0 z-[200] flex items-center justify-center"
          style={{ background: "var(--bg)" }}
          data-testid="intro-splash"
        >
          <div className="absolute inset-0 energy-grid-bg opacity-40" />
          <div className="absolute inset-0 radial-fade opacity-90" />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative flex flex-col items-center gap-6"
          >
            <TafraMark size={92} />

            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="text-center leading-none"
            >
              <div
                className="font-display tracking-[0.45em] font-medium"
                style={{ color: "var(--text)", fontSize: "18px" }}
              >
                T A F R A
              </div>
              <div
                className="mt-2 font-display tracking-[0.6em] text-[10px]"
                style={{ color: "var(--accent-blue)" }}
              >
                E N E R G Y
              </div>
            </motion.div>

            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 140 }}
              transition={{ delay: 0.35, duration: 0.8, ease: "easeOut" }}
              className="h-px relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
              }}
            />

            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.4 }}
              className="font-mono-spec text-[10px] tracking-[0.32em] uppercase"
              style={{ color: "var(--text-soft)" }}
            >
              Initializing · v 2026
            </motion.span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroSplash;
